import { API_BASE_URL } from './constants';

/**
 * Converts a relative image URL to a full absolute URL using the API base URL
 *
 * @param relativePath - The relative path to the image (e.g., "/uploads/profile-pictures/xyz.png")
 * @returns The full absolute URL or null if the path is invalid
 *
 * @example
 * getFullImageUrl('/uploads/profile-pictures/abc.png')
 * // Returns: 'http://localhost:3000/uploads/profile-pictures/abc.png'
 *
 * getFullImageUrl(null)
 * // Returns: null
 *
 * getFullImageUrl('https://s3.amazonaws.com/bucket/image.png')
 * // Returns: 'https://s3.amazonaws.com/bucket/image.png' (already absolute)
 */
export function getFullImageUrl(relativePath: string | null | undefined): string | null {
  // Handle null, undefined, or empty strings
  if (!relativePath || relativePath.trim() === '') {
    return null;
  }

  // If the path is already an absolute URL (http:// or https://), return it as-is
  // This handles future cloud storage URLs
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }

  // Ensure the relative path starts with a forward slash
  const normalizedPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;

  // Combine API base URL with the relative path
  return `${API_BASE_URL}${normalizedPath}`;
}

/**
 * Get initials from a person's name for use as a fallback avatar
 *
 * @param name - The person's full name
 * @returns The initials (max 2 characters) or '??' if no name
 *
 * @example
 * getInitials('John Smith')
 * // Returns: 'JS'
 *
 * getInitials('Alice')
 * // Returns: 'AL'
 *
 * getInitials('')
 * // Returns: '??'
 */
export function getInitials(name: string | undefined | null): string {
  if (!name || name.trim() === '') {
    return '??';
  }

  return name
    .trim()
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}
